import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { GameProvider, useGame, api } from './context/GameContext';
import BottomNav from './components/BottomNav';
import OnboardingCard from './components/OnboardingCard';
import GalleryPage from './pages/GalleryPage';
import EarnPage from './pages/EarnPage';
import UpgradesPage from './pages/UpgradesPage';
import TopPage from './pages/TopPage';
import AuctionPage from './pages/AuctionPage';
import ProfilePage from './pages/ProfilePage';
import { Toaster } from './components/ui/sonner';
import './index.css';

// Deep-link handler: fires once after the app finishes loading.
// Sources (in priority order):
//   1. Telegram WebApp start_param — set when user clicks t.me/<bot>?startapp=...
//   2. URL ?open= query — set when user clicks web_app inline button.
// Recognised values:
//   • "auction" / "auction_<lot_id>" → navigate to /market (Auction tab).
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  useEffect(() => {
    let target = '';
    try {
      const tg = window.Telegram?.WebApp;
      const startParam = tg?.initDataUnsafe?.start_param || '';
      const queryOpen = new URLSearchParams(window.location.search).get('open') || '';
      target = startParam || queryOpen;
    } catch (_) { /* noop */ }
    if (!target) return;
    if (target === 'auction' || target.startsWith('auction_')) {
      navigate('/market', { replace: true });
    } else if (target === 'gallery' || target === 'collect') {
      navigate('/', { replace: true });
    }
    // Future hooks: target === 'profile', 'top', etc.
  }, [navigate]);
  return null;
};

// While the tutorial is still running (player owns < 2 paintings) and
// the player reopens / reloads the app, drag them back to whichever
// screen the active step lives on. This runs ONCE on mount only — we
// intentionally don't react to subsequent `tutorialStep` changes,
// because doing so used to yank the player to a new tab the moment
// they clicked "Claim" in Discovery. The BottomNav highlight handles
// in-session step transitions; this anchor just makes sure cold opens
// land on the right screen so the on-screen prompt is visible.
const TutorialAnchor = () => {
  const navigate = useNavigate();
  const { user, tutorialStep } = useGame();
  const didAnchorRef = React.useRef(false);
  useEffect(() => {
    if (didAnchorRef.current) return;
    if (!user?.id) return; // wait for auth to resolve
    didAnchorRef.current = true; // run at most once per app session
    if (!tutorialStep) return;
    const cardsCount = user?.cards_count || 0;
    if (cardsCount >= 2) return;
    const collectionSteps = new Set(['repair', 'exhibit', 'buy_second']);
    const upgradesSteps = new Set(['upgrade_slots', 'upgrades']);
    const path = window.location.pathname;
    if (collectionSteps.has(tutorialStep) && path !== '/') {
      navigate('/', { replace: true });
    } else if (upgradesSteps.has(tutorialStep) && path !== '/upgrades') {
      navigate('/upgrades', { replace: true });
    }
  }, [user?.id, tutorialStep, navigate, user?.cards_count]);
  return null;
};

const SPLASH_STAGES = {
  connecting: { en: 'Connecting…', ru: 'Подключение…' },
  gallery: { en: 'Loading gallery…', ru: 'Загрузка галереи…' },
  ready: { en: 'Almost ready', ru: 'Почти готово' },
};

const splashLang = () => {
  try {
    const code = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code
      || (typeof navigator !== 'undefined' && navigator.language) || '';
    return String(code).toLowerCase().startsWith('ru') ? 'ru' : 'en';
  } catch {
    return 'en';
  }
};

const LoadingScreen = ({ progress = 0, stage = 'connecting' }) => {
  // Eased display value: follows the real boot progress but keeps a slow
  // creep so the bar never looks frozen while a slow request finishes.
  const [shown, setShown] = useState(0);
  const lang = splashLang();

  useEffect(() => {
    const id = setInterval(() => {
      setShown((prev) => {
        if (progress >= 100) return Math.min(100, prev + Math.max(1.5, (100 - prev) * 0.25));
        const ceiling = Math.min(98, progress + 7);
        if (prev < progress) return Math.min(progress, prev + Math.max(0.8, (progress - prev) * 0.14));
        return Math.min(ceiling, prev + 0.15);
      });
    }, 50);
    return () => clearInterval(id);
  }, [progress]);

  const pct = Math.round(shown);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-10 bg-[#05050A]"
      style={{ backgroundImage: 'radial-gradient(circle at 50% 38%, rgba(196,160,82,0.10), transparent 62%)' }}
      data-testid="loading-screen"
    >
      <div className="w-full max-w-[260px] flex flex-col items-center">
        <div className="relative w-14 h-14 mb-8">
          <div className="absolute inset-0 rounded-full blur-2xl bg-[#C4A052]/15" />
          <img
            src="/logo-transparent.png"
            alt="Private Art"
            className="relative w-full h-full object-contain splash-logo"
          />
        </div>

        <div className="w-full h-[3px] rounded-full bg-white/[0.07] overflow-hidden" data-testid="loading-progress-track">
          <div
            className="h-full rounded-full transition-[width] duration-200 ease-out"
            style={{
              width: `${pct}%`,
              background: 'linear-gradient(90deg, #8A6B22 0%, #C4A052 55%, #F0D9A0 100%)',
              boxShadow: '0 0 12px rgba(196,160,82,0.65)',
            }}
            data-testid="loading-progress-bar"
          />
        </div>

        <div className="w-full mt-3 flex items-center justify-between">
          <span className="text-xs tracking-wide text-white/45" data-testid="loading-stage-label">
            {(SPLASH_STAGES[stage] || SPLASH_STAGES.connecting)[lang]}
          </span>
          <span className="text-xs tabular-nums text-[#C4A052]/90" data-testid="loading-progress-percent">
            {pct}%
          </span>
        </div>
      </div>
      {/* Preload auction image */}
      <img src="/starry-night.jpg" alt="" className="hidden" />
    </div>
  );
};

const ErrorScreen = ({ onRetry }) => {
  // Best-effort language detection from Telegram WebApp before auth
  // completes so the maintenance message reads in the player's
  // language. Falls back to English.
  const lang = (() => {
    try {
      const code = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code
        || (typeof navigator !== 'undefined' && navigator.language) || '';
      return String(code).toLowerCase().startsWith('ru') ? 'ru' : 'en';
    } catch {
      return 'en';
    }
  })();

  const t = lang === 'ru'
    ? {
        title: 'Технические работы',
        body: 'Игра обновляется — мы скоро вернёмся. Спасибо за терпение.',
        cta: 'Попробовать снова',
      }
    : {
        title: 'Under Maintenance',
        body: 'The game is being updated — we’ll be back shortly. Thanks for your patience.',
        cta: 'Try Again',
      };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6">
      <div className="text-center max-w-sm" data-testid="maintenance-screen">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="#D4AF37" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        <h1 className="font-heading text-xl text-[#F5F5F7] mb-2">{t.title}</h1>
        <p className="text-[#A1A1AA] text-sm mb-6">{t.body}</p>
        <button
          data-testid="maintenance-retry"
          onClick={onRetry}
          className="px-6 py-3 rounded-xl btn-gold"
        >
          {t.cta}
        </button>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { loading, error, user, refreshUser, setTutorialStep, peekPrefetch, bootProgress, bootStage } = useGame();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [firstCard, setFirstCard] = useState(null);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.id) {
        setCheckingOnboarding(false);
        return;
      }

      try {
        // Reuse the boot-time prefetch when present — saves ~200-500ms
        // by skipping the duplicate /cards/me + /cards/stats round-trip
        // that GameContext already fired during the splash screen.
        // `peekPrefetch` doesn't drain the cache, so GalleryPage's
        // mount still picks up the same payload (single-use there).
        const peekedCards = peekPrefetch?.('/cards/me');
        const peekedStats = peekPrefetch?.('/cards/stats');
        let userCards = peekedCards;
        let stats = peekedStats;
        if (!userCards || !stats) {
          const [userCardsRes, statsRes] = await Promise.all([
            peekedCards ? Promise.resolve({ data: peekedCards }) : api.get('/cards/me'),
            peekedStats ? Promise.resolve({ data: peekedStats }) : api.get('/cards/stats'),
          ]);
          userCards = userCardsRes.data;
          stats = statsRes.data;
        }

        const userHasCards = (userCards || []).length > 0;
        const cardsAvailable = (stats?.available_count || 0) > 0;

        // Show onboarding if user has no cards AND there are cards available
        if (!userHasCards && cardsAvailable) {
          setShowOnboarding(true);
        }
      } catch (err) {
        console.error('Error checking onboarding:', err);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    if (!loading) {
      checkOnboarding();
    }
  }, [loading, user, peekPrefetch]);

  const handleOnboardingComplete = async (claimedCard) => {
    setShowOnboarding(false);
    if (claimedCard) {
      setFirstCard(claimedCard);
      // Only seed the tutorial if there isn't already a saved step
      // from a previous session — otherwise resuming mid-tutorial
      // would always restart from the first step.
      try {
        const uid = user?.id || 'anon';
        const saved = localStorage.getItem(`pa_tutorial_step:${uid}`);
        if (!saved) setTutorialStep('repair');
      } catch {
        setTutorialStep('repair');
      }
    }
    await refreshUser();
  };

  if (loading || checkingOnboarding) {
    return <LoadingScreen progress={loading ? bootProgress : 100} stage={loading ? bootStage : 'ready'} />;
  }

  if (error && !user) {
    return <ErrorScreen onRetry={() => window.location.reload()} />;
  }

  // Show onboarding for new users
  if (showOnboarding) {
    return <OnboardingCard onComplete={handleOnboardingComplete} />;
  }

  return (
    <div className="min-h-screen bg-[#05050A]">
      <div className="max-w-md mx-auto min-h-screen relative">
        <DeepLinkHandler />
        <TutorialAnchor />
        <Routes>
          <Route path="/" element={<GalleryPage firstCard={firstCard} onFirstCardShown={() => setFirstCard(null)} />} />
          <Route path="/earn" element={<EarnPage />} />
          <Route path="/upgrades" element={<UpgradesPage />} />
          <Route path="/top" element={<TopPage />} />
          <Route path="/market" element={<AuctionPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Routes>
        <BottomNav />
      </div>
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#0C0C14',
            color: '#F5F5F7',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />
    </div>
  );
};

function App() {
  const manifestUrl = `${window.location.origin}/tonconnect-manifest.json`;
  return (
    <TonConnectUIProvider manifestUrl={manifestUrl}>
      <BrowserRouter>
        <GameProvider>
          <AppContent />
        </GameProvider>
      </BrowserRouter>
    </TonConnectUIProvider>
  );
}

export default App;
